import { API } from '@/modules/api'
import type { TAffinetyActivityCartLine } from '@/types/cart'
import { OrderItemType, OrderItemTypeCode } from '@/types/orderItemType'
import { defineStore } from 'pinia'
import type { AffinetyActivityFee } from '@/types/activity'
import { useCartStore } from '@/stores/cart'

export const createUniqueId = (feeId: number) =>
  feeId + '::' + OrderItemTypeCode.AffinetyActivityFee

export const useAffinetyActivityFeesStore = defineStore('affinetyActivityFees', {
  state: () => ({
    isLoaded: {} as Record<TStudentAccountId, boolean>,
    activities: {} as Record<TStudentAccountId, AffinetyActivityFee[]>
  }),

  getters: {
    isLoading(state) {
      return (studentAccountId: TStudentAccountId) => !(state.isLoaded[studentAccountId] ?? false)
    },

    getActivities(state) {
      return (studentAccountId: TStudentAccountId) => state.activities[studentAccountId] ?? []
    }
  },

  actions: {
    async fetch(_districtId: TDistrictId, studentAccountId: TStudentAccountId) {
      try {
        this.isLoaded[studentAccountId] = false
        this.activities[studentAccountId] = await API.Get.affinetyActivityFees(studentAccountId)
        this.validateCart(_districtId, studentAccountId)
        this.isLoaded[studentAccountId] = true
      } catch (e) {
        this.isLoaded[studentAccountId] = true
        throw e
      }
    },

    // This function is used to validate the cart lines for the affinety activity fees store
    // It checks if the cart lines are valid compared to the data from the API, and if not, it removes them from the cart.
    // This prevents the cart from attempting to get order line estimates or checking out with invalid data.
    validateCart(districtId: TDistrictId, studentAccountId: TStudentAccountId) {
      const cartStore = useCartStore()

      for (const line of cartStore.getCartLines(districtId) as TAffinetyActivityCartLine[]) {
        if (
          line.orderItemType === OrderItemType.AffinetyActivityFee &&
          line.studentAccountId === studentAccountId
        ) {
          const activity = this.activities[studentAccountId].find(
            activity => activity.feeId === line.feeId
          )
          if (!activity) {
            console.log('activity not found, removing from cart', line.feeId)
            this.removeFromCart(districtId, line.feeId)
          }
          // ensure fee amount is less than cart amountn meaning cart amount is too high
          if (activity && activity.amountDue < line.amount) {
            console.log('activity amount is less than cart amount, adjusting cart', line.feeId)
            cartStore.updateCartLine(districtId, line.uniqueId, {
              ...line,
              amount: activity.amountDue
            })
          }
        }
      }
    },

    addToCart(districtId: TDistrictId, activityData: Omit<TAffinetyActivityCartLine, 'uniqueId'>) {
      const cartStore = useCartStore()
      const uniqueId = createUniqueId(activityData.feeId)
      const existingLine = cartStore.getCartLine(districtId, uniqueId)

      if (existingLine) {
        cartStore.updateCartLine(districtId, uniqueId, activityData)
      } else {
        cartStore.addCartLine(districtId, uniqueId, { ...activityData, uniqueId })
      }
    },

    updateCartLine(
      districtId: TDistrictId,
      activityData: Partial<TAffinetyActivityCartLine> & { feeId: number }
    ) {
      const cartStore = useCartStore()
      const uniqueId = createUniqueId(activityData.feeId)
      const existingLine = cartStore.getCartLine(districtId, uniqueId)
      if (!existingLine) {
        console.debug('uniqueId: ', uniqueId)
        console.debug('activityData: ', activityData)
        throw new Error('Could not update affinety activity fee, existing cart line not found.')
      }
      cartStore.updateCartLine(districtId, uniqueId, activityData)
    },

    removeFromCart(districtId: TDistrictId, feeId: number) {
      const cartStore = useCartStore()
      const uniqueId = createUniqueId(feeId)
      cartStore.removeCartLine(districtId, uniqueId)
    }
  }
})
